// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Box, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ExecutedPauseStateChange } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Model/MarkerEvents';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useCsvExport } from '../../../hooks/other/useCsvExport';
import usePauseHistory from '../../../hooks/queries/registry/security/usePauseHistory';
import TemplateContract from '../../../utils/TemplateContract';
import { formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';

type Row = TemplateContract<ExecutedPauseStateChange>;

interface PauseHistoryProps {
  disableVirtualization?: boolean;
}

const columns: GridColDef<Row>[] = [
  {
    field: 'instrumentId',
    headerName: 'Token',
    valueGetter: (params) => params.row.payload.instrumentId.id,
    flex: 1,
    filterable: true,
  },
  {
    field: 'state',
    headerName: 'State',
    valueGetter: (params) => params.row.payload.action,
    flex: 0.5,
    filterable: true,
  },
  {
    field: 'instrumentSecurityControl',
    headerName: 'Instrument Security Control',
    valueGetter: (params) => params.row.payload.instrumentSecurityControl,
    renderCell: (params) => <PartyDisplay party={params.row.payload.instrumentSecurityControl} />,
    flex: 1,
    filterable: false,
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    type: 'dateTime',
    valueGetter: (params) => parseTimestampToDate(params.row.createdAt),
    renderCell: (params) => formatTimestamp(params.row.createdAt),
    flex: 1,
    filterable: true,
  },
];

export const PauseHistory: React.FC<PauseHistoryProps> = ({ disableVirtualization }) => {
  const pauseHistory = usePauseHistory();

  const rows = pauseHistory.data ?? [];

  const { handleExport, isExportDisabled } = useCsvExport(columns, rows, {
    filename: 'pause_history',
  });

  const buttons = [
    {
      name: 'Export CSV',
      onClick: handleExport,
      disabled: isExportDisabled,
    },
  ];

  return (
    <PageWrapper
      buttons={buttons}
      pageName={
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1 }}>
          <Typography
            sx={{ textTransform: 'capitalize' }}
            fontWeight="bold"
            color="primary"
            variant="h4"
          >
            Pause/Unpause History
          </Typography>
        </Box>
      }
    >
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.contractId}
        defaultSortModel={[{ field: 'createdAt', sort: 'desc' }]}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
